/// <amd-module name="Wit/WorkItemForm/Common/Components/MarkdownConversionPreviewDialog" />
import * as React from "react";
import * as TurndownService from "turndown";
import { ObservableValue } from "VSS/Core/Observable";
import { Dialog } from "VSSUI/Dialog";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { CommentToConvert } from "Wit/WorkItemForm/Contracts";
import { WorkItemFormCommentPreview } from "Wit/WorkItemForm/Details/WorkItemFormCommentPreview";
import * as Resources from "Wit/WorkItemForm/Resources";
import { convertMentions } from "Wit/WorkItemFormMarkdown/MarkdownUtils";

export interface IMarkdownConversionPreviewDialogProps {
    commentToConvert: ObservableValue<CommentToConvert | undefined>;
}

export const convertHtmlToMarkdown = (htmlContent: string): Promise<string> => {
    return new Promise((resolve, reject) => {
        try {
            const turndownService = new TurndownService();
            const result = turndownService.turndown(htmlContent);
            resolve(result);
        } catch (error) {
            reject(error);
        }
    });
};

export function MarkdownConversionPreviewDialog({ commentToConvert }: IMarkdownConversionPreviewDialogProps) {
    const [markdown, setMarkdown] = React.useState<string>();
    const [convertingError, setConvertingError] = React.useState<boolean>(false);
    const [isConverting, setIsConverting] = React.useState<boolean>(true);

    const htmlContent = commentToConvert.value?.htmlComment;
    React.useEffect(() => {
        const convert = async (): Promise<void> => {
            if (htmlContent) {
                setMarkdown(undefined);
                setConvertingError(false);
                try {
                    const htmlContentWithConvertedMentions = await convertMentions(htmlContent);
                    const markdown = await convertHtmlToMarkdown(htmlContentWithConvertedMentions);
                    setMarkdown(markdown);
                } catch (error) {
                    setConvertingError(true);
                }
            } else {
                setMarkdown(undefined);
            }
            setIsConverting(false);
        };

        setTimeout(() => convert(), 0);
    }, [htmlContent]);

    return commentToConvert.value?.htmlComment ? (
        <Dialog
            titleProps={{ text: Resources.ConvertedCommentTitle }}
            onDismiss={() => {
                commentToConvert.value = undefined;
            }}
            footerButtonProps={[
                {
                    text: Resources.LabelCancel,
                    onClick: () => {
                        commentToConvert.value = undefined;
                    }
                },
                {
                    text: Resources.LabelConvert,
                    primary: true,
                    disabled: markdown === undefined,
                    onClick: () => {
                        commentToConvert.value = {
                            markdownComment: markdown,
                            htmlComment: commentToConvert.value!.htmlComment
                        };
                    }
                }
            ]}
            calloutContentClassName="markdown-conversion-preview-dialog-callout-content"
        >
            {markdown !== undefined ? (
                <WorkItemFormCommentPreview content={markdown} refreshThrottleMs={undefined} setContentOnInitialRender />
            ) : convertingError ? (
                <MessageCard severity={MessageCardSeverity.Error}>{Resources.CommentConvertingError}</MessageCard>
            ) : isConverting ? (
                <Spinner size={SpinnerSize.small} label={Resources.CommentConverting} />
            ) : null}
        </Dialog>
    ) : null;
}
